import Navbar from "../Navbar/navbar";
import Footer from "../Footer/footer";
import { React, useEffect, useState } from "react";
import { useSwipeable } from "react-swipeable";
import { useNavigate } from "react-router-dom";
import icons from "../../icons.config";
import { useDispatch, useSelector } from "react-redux";
import {
  addToFavourites,
  removeFromFavourites,
} from "../../Providers/favouritesProvider.js";
import { setClickedItem } from "../../store/itemsSlice";
import { getAllProducts, getAllFilters } from "../../Providers/shopProvider.js";
import FooterDoodles from "../Footer/footerDoodles";
import TrendWavesSection from "./trendWavesSection";
import FilterPanel from "./filterPanel";
import SortDropdown from "./sortDropDown";
import ProductGrid from "./productGrid";
import LoginPromptPopup from "../ProductPage/loginPromptPopup";

function Shop({ item }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleItems, setVisibleItems] = useState(3);
  const [isFading, setIsFading] = useState(false);
  const [showLoginPrompt, setShowLoginPrompt] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isPriceOpen, setIsPriceOpen] = useState(true);
  const [isSizeOpen, setIsSizeOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [isColorOpen, setIsColorOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedSort, setSelectedSort] = useState("");
  const [gridType, setGridType] = useState("double");
  const [gridIcon, setGridIcon] = useState({
    enlarge: icons.viewEnlargeLight,
    small: icons.viewSmallLight,
  });

  const sortOptions = ["Price: Low to High", "Price: High to Low", "Latest"];

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated || false);
  const likedItems = useSelector((state) => state?.favourites?.likedItems || []);

  const [tempFilters, setTempFilters] = useState({
    sizes: [],
    colors: [],
    min_price: "",
    max_price: "",
  });
  const [selectedFilters, setSelectedFilters] = useState({
    sizes: [],
    colors: [],
    min_price: "",
    max_price: "",
  });
  const [priceFilter, setPriceFilter] = useState({ min: "", max: "" });
  const [trendWavesData, setTrendWavesData] = useState([]);
  const [productGridData, setProductGridData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filterOptions, setFilterOptions] = useState({
    sizes: [],
    colors: [],
    min_price: 0,
    max_price: 0,
  });

  const handlePriceFilterChange = (newPriceFilter) => {
    setPriceFilter(newPriceFilter);
    setTempFilters((prev) => ({
      ...prev,
      min_price: newPriceFilter.min,
      max_price: newPriceFilter.max,
    }));
  };

  const handleSizeChange = (size) => {
    setTempFilters((prev) => {
      const newSizes = prev.sizes.includes(size)
        ? prev.sizes.filter((s) => s !== size)
        : [...prev.sizes, size];
      return { ...prev, sizes: newSizes };
    });
  };

  const handleColorChange = (color) => {
    setTempFilters((prev) => {
      const newColors = prev.colors.includes(color)
        ? prev.colors.filter((c) => c !== color)
        : [...prev.colors, color];
      return { ...prev, colors: newColors };
    });
  };

  const applyFilters = async () => {
    setLoading(true); // Start loading immediately
    setSelectedFilters(tempFilters); // Update selected filters
    setSelectedSort(selectedSort); // Ensure sort is applied
    await fetchProductGridData(tempFilters, selectedSort); // Pass filters directly to ensure immediate use
    toggleFilter(); // Close the filter panel
  };

  const fetchTrendWavesData = async () => {
    try {
      const response = await getAllProducts();
      const validItems = response.data.filter((item) => item.default_variant !== null);
      setTrendWavesData(validItems);
    } catch (err) {
      console.error("Error fetching TrendWaves data:", err);
    }
  };

  // Example state additions (add these in your component)
  const [noProductsMessage, setNoProductsMessage] = useState(null);
  
  const fetchProductGridData = async (filters = selectedFilters, sort = selectedSort) => {
    setLoading(true);
    setError(null);
    setNoProductsMessage(null); // New state to handle no-products message
  
    try {
      const queryParams = new URLSearchParams();
      filters.sizes.forEach((size) => queryParams.append("size", size));
      filters.colors.forEach((color) => queryParams.append("color", color));
      if (filters.min_price) {
        queryParams.append("min_price", filters.min_price);
      }
      if (filters.max_price) {
        queryParams.append("max_price", filters.max_price);
      }
      if (sort) {
        switch (sort) {
          case "Price: High to Low":
            queryParams.append("sort_option", "high-to-low");
            break;
          case "Price: Low to High":
            queryParams.append("sort_option", "low-to-high");
            break;
          case "Latest":
            queryParams.append("sort_option", "latest");
            break;
          default:
            break;
        }
      }
      const queryString = queryParams.toString();
      console.log("Fetching ProductGrid with query:", queryString);
  
      const productResponse = await getAllProducts(queryString ? `?${queryString}` : "");
      const validItems = productResponse.data.filter((item) => item.default_variant !== null);
  
      if (validItems.length === 0 && queryString) {
        // No products match the filters
        setNoProductsMessage({
          text: "No products available with your desired filters!! Returning all products",
          icon: <icons.sizeAdded />, // Adjust based on actual icon component
        });
        setProductGridData([]); // Clear current products
  
        // Wait 3-4 seconds, then fetch all products
        setTimeout(async () => {
          try {
            const fallbackResponse = await getAllProducts(); // Fetch without filters
            const validFallbackItems = fallbackResponse.data.filter(
              (item) => item.default_variant !== null
            );
            setProductGridData(validFallbackItems);
            setNoProductsMessage(null); // Clear message after loading all products
          } catch (fallbackErr) {
            console.error("Fallback fetch failed:", fallbackErr);
            setError("Failed to load products. Please try again.");
            setProductGridData([]);
          }
        }, 3500); // 3.5-second delay
      } else {
        // Products found, set them directly
        setProductGridData(validItems);
      }
  
      if (
        filterOptions.sizes.length === 0 &&
        filterOptions.colors.length === 0 &&
        filterOptions.min_price === 0 &&
        filterOptions.max_price === 0
      ) {
        const filterResponse = await getAllFilters();
        const options = filterResponse?.data || {
          sizes: [],
          colors: [],
          min_price: 0,
          max_price: 0,
        };
        setFilterOptions(options);
  
        const initialPriceFilter = {
          min: options.min_price ? String(options.min_price) : "",
          max: options.max_price ? String(options.max_price) : "",
        };
        setPriceFilter(initialPriceFilter);
        setTempFilters((prev) => ({
          ...prev,
          min_price: initialPriceFilter.min,
          max_price: initialPriceFilter.max,
        }));
        setSelectedFilters((prev) => ({
          ...prev,
          min_price: initialPriceFilter.min,
          max_price: initialPriceFilter.max,
        }));
      }
    } catch (err) {
      console.error("Error fetching ProductGrid data:", err);
      if (err.response?.status === 400) {
        // Handle 400 error as no products found
        setNoProductsMessage({
          text: "No products available with your desired filters",
          icon: <icons.sizeAdded />, // Adjust based on actual icon component
        });
        setProductGridData([]);
  
        // Wait 3-4 seconds, then fetch all products
        setTimeout(async () => {
          try {
            const fallbackResponse = await getAllProducts(); // Fetch without filters
            const validFallbackItems = fallbackResponse.data.filter(
              (item) => item.default_variant !== null
            );
            setProductGridData(validFallbackItems);
            setNoProductsMessage(null); // Clear message after loading all products
          } catch (fallbackErr) {
            console.error("Fallback fetch failed:", fallbackErr);
            setError("Failed to load products. Please try again.");
            setProductGridData([]);
          }
        }, 3500); // 3.5-second delay
      } else {
        // Other errors
        setError(err.message || "Error fetching data. Showing unfiltered products.");
        try {
          const fallbackResponse = await getAllProducts();
          const validFallbackItems = fallbackResponse.data.filter(
            (item) => item.default_variant !== null
          );
          setProductGridData(validFallbackItems);
        } catch (fallbackErr) {
          console.error("Fallback fetch failed:", fallbackErr);
          setProductGridData([]);
        }
      }
    } finally {
      setLoading(false);
    }
  };
  


  useEffect(() => {
    fetchTrendWavesData();
    fetchProductGridData();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setVisibleItems(window.innerWidth < 1024 ? 2 : 3);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleNext = () => {
    const trendWavesItems = trendWavesData.filter((item) =>
      item.product_category.includes("Trend Waves")
    );
    setIsFading(true);
    setTimeout(() => {
      setCurrentIndex((prevIndex) =>
        (prevIndex + visibleItems) % trendWavesItems.length === 0
          ? 0
          : prevIndex + 1
      );
      setIsFading(false);
    }, 500);
  };

  const handlePrev = () => {
    const trendWavesItems = trendWavesData.filter((item) =>
      item.product_category.includes("Trend Waves")
    );
    setIsFading(true);
    setTimeout(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === 0
          ? Math.max(0, trendWavesItems.length - visibleItems)
          : Math.max(0, prevIndex - 1)
      );
      setIsFading(false);
    }, 500);
  };

  const getVisibleItems = () => {
    const filteredItems = trendWavesData.filter((item) =>
      item.product_category.includes("Trend Waves")
    );
    if (filteredItems.length === 0) return [];
    const startIndex = currentIndex;
    const endIndex = Math.min(startIndex + visibleItems, filteredItems.length);
    return filteredItems.slice(startIndex, endIndex);
  };

  const trendWavesItems = trendWavesData.filter((item) =>
    item.product_category.includes("Trend Waves")
  );
  const numberOfDots = Math.ceil(trendWavesItems.length / visibleItems);

  const handleDotClick = (index) => {
    setIsFading(true);
    setTimeout(() => {
      setCurrentIndex(index * visibleItems);
      setIsFading(false);
    }, 500);
  };

  const handlers = useSwipeable({
    onSwipedLeft: handleNext,
    onSwipedRight: handlePrev,
    preventDefaultTouchmoveEvent: true,
    trackMouse: true,
  });

  const handleItemClick = (item) => {
    dispatch(setClickedItem(item));
    const formatText = (text) => encodeURIComponent(text.replace(/\s+/g, ""));
    const category = formatText(item.product_category[0].toLowerCase());
    const productName = formatText(item.product_name);
    const variant = formatText(item.default_variant.title);
    const variantId = item.default_variant.id;
    navigate(`/${category}/${productName}/${variant}/${variantId}/buy`);
  };

  const handleHeartClick = async (item) => {
    if (!isAuthenticated) {
      setShowLoginPrompt(true);
      return;
    }
    const likedItem = likedItems.find(
      (likedItem) => String(likedItem.variant_id) === String(item.default_variant.id)
    );
    if (likedItem) {
      dispatch(removeFromFavourites(item.default_variant.id));
    } else {
      dispatch(addToFavourites(item.default_variant.id));
    }
  };

  const toggleFilter = () => setIsFilterOpen(!isFilterOpen);
  const togglePrice = () => setIsPriceOpen(!isPriceOpen);
  const toggleSize = () => setIsSizeOpen(!isSizeOpen);
  const toggleColor = () => setIsColorOpen(!isColorOpen);
  const toggleSort = () => setIsSortOpen((prev) => !prev);
  const handleSortOptionClick = (option) => {
    setSelectedSort(option);
    setIsDropdownOpen(false);
  };

  const clearAllFilters = () => {
    setPriceFilter({ min: "", max: "" });
    setTempFilters({ sizes: [], colors: [], min_price: "", max_price: "" });
    setSelectedFilters({ sizes: [], colors: [], min_price: "", max_price: "" });
    setSelectedSort("");
    fetchProductGridData({ sizes: [], colors: [], min_price: "", max_price: "" }, ""); // Reset to unfiltered data
  };

  const handleRemoveFilter = (type, value) => {
    if (type === "price") {
      setPriceFilter({ min: "", max: "" });
      setTempFilters((prev) => ({ ...prev, min_price: "", max_price: "" }));
    } else if (type === "size") {
      setTempFilters((prev) => ({
        ...prev,
        sizes: prev.sizes.filter((size) => size !== value),
      }));
    } else if (type === "color") {
      setTempFilters((prev) => ({
        ...prev,
        colors: prev.colors.filter((color) => color !== value),
      }));
    }
  };

  const handleGridToggle = (type) => {
    if (type === "single") {
      setGridType("single");
      setGridIcon({ enlarge: icons.viewEnlarge, small: icons.viewSmallLight });
    } else {
      setGridType("double");
      setGridIcon({ enlarge: icons.viewEnlargeLight, small: icons.viewSmall });
    }
  };

  return (
    <div className="overflow-hidden">
      <Navbar />
      <section className="flex flex-col justify-center items-center w-full h-full bg-tertiary pt-60 lg:pt-5 relative pb-0 space-y-5 overflow-hidden">
        <div className="hidden lg:block relative lg:left-[45%] z-10">
          <img src={icons.trendCircles} alt="Doodle Left" className="w-[70%]" />
        </div>
      </section>
      <TrendWavesSection
        getVisibleItems={getVisibleItems}
        visibleItems={visibleItems}
        isFading={isFading}
        handleNext={handleNext}
        handlePrev={handlePrev}
        handleDotClick={handleDotClick}
        currentIndex={currentIndex}
        numberOfDots={numberOfDots}
        handlers={handlers}
        handleItemClick={handleItemClick}
        handleHeartClick={handleHeartClick}
        likedItems={likedItems}
        isAuthenticated={isAuthenticated}
      />
      <section className="bg-tertiary relative py-2 lg:py-0">
        <FilterPanel
          isFilterOpen={isFilterOpen}
          toggleFilter={toggleFilter}
          appliedFilterCount={
            (priceFilter.min || priceFilter.max ? 1 : 0) +
            tempFilters.sizes.length +
            tempFilters.colors.length
          }
          priceFilter={priceFilter}
          setPriceFilter={handlePriceFilterChange}
          isPriceOpen={isPriceOpen}
          togglePrice={togglePrice}
          isSizeOpen={isSizeOpen}
          toggleSize={toggleSize}
          appliedFilters={tempFilters}
          handleSizeChange={handleSizeChange}
          clearAllFilters={clearAllFilters}
          handleRemoveFilter={handleRemoveFilter}
          toggleSort={toggleSort}
          isSortOpen={isSortOpen}
          handleSortOptionClick={handleSortOptionClick}
          filterOptions={filterOptions}
          isColorOpen={isColorOpen}
          toggleColor={toggleColor}
          handleColorChange={handleColorChange}
          sortOptions={sortOptions}
          selectedSort={selectedSort}
          applyFilters={applyFilters}
        />
        <div>
          <div className="hidden lg:block relative left-[5%] bottom-16 justify-start items-start">
            <img src={icons.collectionFlower} alt="Doodle Left" className="lg:w-24" />
          </div>
          <h1 className="flex text-primary1 font-poppins text-3xl lg:py-4 mt-2 font-extralight mb-5 relative text-center justify-center bottom-1 lg:bottom-20">
            OUR COLLECTIONS
          </h1>
          <div className="flex flex-col justify-center items-center">
            <div
              className={`relative ${
                isFilterOpen
                  ? "left-0"
                  : "right-[29%] md:right-[38%] lg:right-[37%] xl:right-[38%]"
              } 
              ${isFilterOpen ? "bottom-20" : "-bottom-4 lg:bottom-20"} 
              flex items-center transition-all duration-300 ease-in-out`}
              onClick={toggleFilter}
            >
              {isFilterOpen ? null : (
                <>
                  <img src={icons.Filter} alt="filter icon" className="mr-2 h-6 w-6 lg:h-7 lg:w-7" />
                  <h2 className="font-outfit font-light text-xl md:text-2xl cursor-pointer">
                    Filters
                  </h2>
                </>
              )}
            </div>
            <SortDropdown
              isDropdownOpen={isDropdownOpen}
              setIsDropdownOpen={setIsDropdownOpen}
              selectedSort={selectedSort}
              handleSortOptionClick={handleSortOptionClick}
              sortOptions={sortOptions}
            />
            <div className="lg:hidden relative left-[30%] md:left-[39%] bottom-2 flex items-center">
              <img
                src={gridIcon.enlarge}
                alt="enlarge view"
                className="mr-2 w-6 h-6 cursor-pointer"
                onClick={() => handleGridToggle("single")}
              />
            </div>
            <div className="lg:hidden relative left-[38%] md:left-[44%] bottom-9 pt-1 flex items-center">
              <img
                src={gridIcon.small}
                alt="small view"
                className="mr-2 w-6 h-6 cursor-pointer"
                onClick={() => handleGridToggle("double")}
              />
            </div>
            <ProductGrid
              itemData={productGridData}
              loading={loading}
              error={error}
              gridType={gridType}
              handleGridToggle={handleGridToggle}
              gridIcon={gridIcon}
              handleItemClick={handleItemClick}
              handleHeartClick={handleHeartClick}
              likedItems={likedItems}
              isAuthenticated={isAuthenticated}
              isFilterOpen={isFilterOpen}
              handlers={handlers}
            />
            <div className="lg:hidden">
              <div className="absolute left-0 top-24 z-0">
                <img src={icons.collectionMCircle} alt="Doodle Left" className="w-full" />
              </div>
              <div className="absolute right-6 bottom-16">
                <img src={icons.collectionMFlower} alt="Doodle Right" className="w-full" />
              </div>
            </div>
            <div className="hidden lg:block absolute md:-right-0 xl:right-16 bottom-0">
              <img src={icons.collectionFlower} alt="Doodle Left" className="lg:w-3/4" />
            </div>
          </div>
        </div>
      </section>
      <FooterDoodles />
      <Footer />
      {showLoginPrompt && (
        <LoginPromptPopup
          message="Please log in first to add items to your wishlist!"
          onClose={() => setShowLoginPrompt(false)}
        />
      )}
    </div>
  );
}

export default Shop;